import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { ScrollArea } from "./ui/scroll-area";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import {
  MessageCircle,
  Bell,
  X,
  ExternalLink,
  Volume2,
  VolumeX,
  Check
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// Sound for notifications (optional)
const playNotificationSound = () => {
  try {
    const audio = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Onp6clYx9aF1kc4OUo6abkH9tYmhzg5OfoZqNfm1laHODlJ+gmI19bWVpc4KUn5+YjX1sZWlzgpSfn5iNfWxlaXOClJ+fmI19bGVpc4KUn5+YjXxsZWlzgpSfn5iNfGxlaXOClJ+fmI18bGVpc4KUn5+YjXxsZWlzgpSfn5iNfGxlaXOClJ+fmI18bGVpc4KUn5+YjXxsZWlzgpSfn5iNfGxlaXOClJ+fmI18bGVpc4KUn5+YjXxsZQ==");
    audio.volume = 0.3;
    audio.play().catch(() => {});
  } catch (e) {
    // Ignore audio errors
  }
};

export default function WhatsAppNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastCheckTime, setLastCheckTime] = useState(new Date().toISOString());
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const previousUnreadRef = useRef(0);

  const fetchNewMessages = useCallback(async () => {
    if (!user) return;
    
    try {
      // Get conversations with unread messages
      const res = await axios.get(`${API}/whatsapp/conversations?limit=20`, getAuthHeaders());
      const conversations = res.data?.conversations || [];
      
      // Filter conversations with unread messages
      const unreadConversations = conversations.filter(c => c.unread_count > 0);
      const totalUnread = unreadConversations.reduce((sum, c) => sum + c.unread_count, 0);
      
      // Check if there are new messages since last check
      const newNotifications = unreadConversations.map(conv => ({
        id: conv.id,
        contact_name: conv.contact_name || conv.contact_phone,
        contact_phone: conv.contact_phone,
        message: conv.last_message,
        time: conv.last_message_at,
        unread_count: conv.unread_count,
        config_id: conv.config_id
      }));
      
      // Play sound if new messages arrived
      if (soundEnabled && totalUnread > previousUnreadRef.current && previousUnreadRef.current >= 0) {
        playNotificationSound();
        
        // Show browser notification if permitted
        if (typeof Notification !== 'undefined' && Notification.permission === "granted" && newNotifications.length > 0) {
          const latestNotif = newNotifications[0];
          new Notification("Nuevo mensaje de WhatsApp", {
            body: `${latestNotif.contact_name}: ${latestNotif.message}`,
            icon: "/favicon.ico",
            tag: "whatsapp-notification"
          });
        }
      }
      
      previousUnreadRef.current = totalUnread;
      setNotifications(newNotifications);
      setUnreadCount(totalUnread);
      setLastCheckTime(new Date().toISOString());
    } catch (err) {
      console.error("Error fetching WhatsApp notifications:", err);
    }
  }, [getAuthHeaders, user, soundEnabled]);

  useEffect(() => {
    // Request notification permission
    if (typeof Notification !== 'undefined' && Notification.permission === "default") {
      Notification.requestPermission();
    }
    
    // Poll every 15 seconds
    const interval = setInterval(fetchNewMessages, 15000);
    
    // Initial fetch with small delay to avoid cascade
    const timeout = setTimeout(fetchNewMessages, 100);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [fetchNewMessages]);

  const markAllAsRead = async () => {
    try {
      // Mark all conversations as read
      for (const notif of notifications) {
        await axios.post(`${API}/whatsapp/conversations/${notif.id}/read`, {}, getAuthHeaders());
      }
      setNotifications([]);
      setUnreadCount(0);
      previousUnreadRef.current = 0;
      toast.success("Todos los mensajes marcados como leídos");
    } catch (err) {
      toast.error("Error al marcar como leídos");
    }
  };

  const goToConversation = (notif) => {
    setIsOpen(false);
    navigate("/whatsapp/bandeja");
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return "Ahora";
    if (diffMins < 60) return `Hace ${diffMins}m`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    return date.toLocaleDateString("es", { day: "2-digit", month: "short" });
  };

  // Don't render if no user
  if (!user) return null;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="relative"
          data-testid="whatsapp-notifications-button"
        >
          <MessageCircle className="w-5 h-5 text-green-600" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-3 border-b bg-green-50 dark:bg-green-900/20">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-green-600" />
            <span className="font-semibold text-sm">WhatsApp</span>
            {unreadCount > 0 && (
              <Badge className="bg-red-500 text-xs">{unreadCount}</Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => setSoundEnabled(!soundEnabled)}
              title={soundEnabled ? "Silenciar" : "Activar sonido"}
            >
              {soundEnabled ? (
                <Volume2 className="w-4 h-4 text-slate-500" />
              ) : (
                <VolumeX className="w-4 h-4 text-slate-400" />
              )}
            </Button>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={markAllAsRead}
                title="Marcar todo como leído"
              >
                <Check className="w-4 h-4 text-slate-500" />
              </Button>
            )}
          </div>
        </div>
        
        <ScrollArea className="max-h-80">
          {notifications.length === 0 ? (
            <div className="p-6 text-center text-slate-500">
              <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No hay mensajes nuevos</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notif) => (
                <div
                  key={notif.id}
                  className="p-3 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                  onClick={() => goToConversation(notif)}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-green-700">
                        {notif.contact_name?.[0]?.toUpperCase() || notif.contact_phone?.slice(-2)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm text-slate-900 dark:text-white truncate">
                          {notif.contact_name || notif.contact_phone}
                        </span>
                        <span className="text-xs text-slate-400 flex-shrink-0 ml-2">
                          {formatTime(notif.time)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 truncate mt-0.5">
                        {notif.message}
                      </p>
                      {notif.unread_count > 1 && (
                        <Badge className="bg-green-500 text-xs mt-1">
                          {notif.unread_count} mensajes
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        
        <div className="p-2 border-t">
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full text-green-600 hover:text-green-700 hover:bg-green-50"
            onClick={() => {
              setIsOpen(false);
              navigate("/whatsapp/bandeja");
            }}
          >
            Ver todas las conversaciones
            <ExternalLink className="w-3 h-3 ml-2" />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
